import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Calendar, Wrench, FileText, Shield, TrendingUp, Clock, 
  CheckCircle, Receipt, BarChart3, Users, Zap, Cloud, Smartphone, 
  HeartHandshake, Lock, Target, Sparkles, Monitor, Phone, Mail, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { schemas } from '../components/SEO';

// Client logos
const LOGO_BASE = 'https://equiprwebsitestorage.blob.core.windows.net/website-assets/logos';
const clientLogos = [
  { name: 'ABC Crane Hire', url: `${LOGO_BASE}/ABC-Crane-Hire.png` },
  { name: 'Aussie Enviro', url: `${LOGO_BASE}/aussie-enviro.png` },
  { name: 'Bebrok Excavations', url: `${LOGO_BASE}/bebrok-excavations.png` },
  { name: 'Big Hill Cranes', url: `${LOGO_BASE}/bighiillcranes-gray.png` },
  { name: 'Borger Cranes', url: `${LOGO_BASE}/borger-cranes-gray.png` },
  { name: 'Brancatella', url: `${LOGO_BASE}/brancatella.png` },
  { name: 'Brefni', url: `${LOGO_BASE}/brefni-gray.png` },
  { name: 'Carsburg Earthmoving', url: `${LOGO_BASE}/carsburg-earthmoving.png` },
  { name: 'Crossy Cranes', url: `${LOGO_BASE}/crossy-cranes.png` },
  { name: 'Direct Cranes', url: `${LOGO_BASE}/direct-cranes.png` },
  { name: 'Divalls Earthmoving', url: `${LOGO_BASE}/divalls-earthmoving.png` },
  { name: 'Eagle Alliance', url: `${LOGO_BASE}/eagle-alliance-gray.webp` },
  { name: 'Everwilling Cranes', url: `${LOGO_BASE}/everwilling-cranes.png` },
  { name: 'Herbies Earthmoving', url: `${LOGO_BASE}/herbies-earthmoving.png` },
  { name: 'Jet Excavators', url: `${LOGO_BASE}/jet-excavators.jpg` },
  { name: 'LD Earthmoving', url: `${LOGO_BASE}/ld-earthmoving.png` },
  { name: 'M&K Plant Hire', url: `${LOGO_BASE}/m%26k-plant-hire.jpg` },
  { name: 'Morgans Cranes', url: `${LOGO_BASE}/morgans-cranes.png` },
  { name: 'Riverina Cranes', url: `${LOGO_BASE}/riverina-cranes-gray.jpg` },
  { name: 'Surf City Cranes', url: `${LOGO_BASE}/surf-city-cranes-gray.jpg` },
  { name: 'SYMAL', url: `${LOGO_BASE}/symal.jpg` },
  { name: 'Top Gun Cranes', url: `${LOGO_BASE}/top-gun-cranes-gray.jpg` },
  { name: 'Two Way Cranes', url: `${LOGO_BASE}/two-way-cranes.png` },
  { name: 'Urban Crane Hire', url: `${LOGO_BASE}/urban-crane-hire.png` },
  { name: 'Urban Cranes', url: `${LOGO_BASE}/Urban-Cranes.png` },
  { name: 'Vamp Cranes', url: `${LOGO_BASE}/vamp-cranes-gray.jpg` },
  { name: 'Working Bucket', url: `${LOGO_BASE}/working-bucket.png` },
];

const platformHighlights = [
  { icon: Target, label: 'Purpose-Built' },
  { icon: Cloud, label: 'Cloud' },
  { icon: Monitor, label: 'Desktop' },
  { icon: Smartphone, label: 'Mobile App' },
  { icon: Lock, label: 'Secure' },
  { icon: HeartHandshake, label: 'Local Support' },
];

const keyCapabilities = [
  {
    icon: Calendar,
    title: 'Allocations & Scheduling',
    description: 'View all assets, crews, and job allocations on a single drag-and-drop board. Allocate equipment, operators, and support vehicles in just a few clicks.',
    link: '/allocations-scheduling-operated-plant-hire',
  },
  {
    icon: FileText,
    title: 'Mobile Dockets',
    description: 'Send jobs instantly to field teams via the EQUIPR Operator app. Capture real-time dockets, timesheets, and job status updates straight from site.',
    link: '/operator-mobile-dockets-timesheets',
  },
  {
    icon: Receipt,
    title: 'Invoicing',
    description: 'Create and send invoices directly from the job schedule. Automatically attach completed job data and reduce delays with faster invoice turnaround.',
    link: '/allocations-scheduling-operated-plant-hire',
  },
  {
    icon: Shield,
    title: 'Compliance Tracking',
    description: 'Track licences, inductions and safety documentation with automated alerts and visual flags - ensuring every job is compliant.',
    link: '/guardian',
  },
  {
    icon: BarChart3,
    title: 'Reporting & Insights',
    description: 'Access real-time reports on equipment utilisation, job profitability, invoice performance, and more. Customise views to suit operational needs.',
    link: '/allocations-scheduling-operated-plant-hire',
  },
  {
    icon: Users,
    title: 'Payroll & Timesheets',
    description: 'Reconcile daily jobs with timesheets using side-by-side views. Approve hours worked and streamline payroll processing with accurate, synced data.',
    link: '/operator-mobile-dockets-timesheets',
  },
];

const featuresAndBenefits = [
  {
    icon: Zap,
    title: 'Streamlined Workflow',
    description: 'Bring your entire operation into one streamlined system. Eliminate paperwork and improve team communication.',
  },
  {
    icon: TrendingUp,
    title: 'Improved Cash Flow',
    description: 'Faster invoice turnaround with automatic job data attachment. Reduce delays and get paid quicker.',
  },
  {
    icon: Clock,
    title: 'Time Savings',
    description: 'Less admin for your office and field teams. Smarter resource utilisation and faster decision-making.',
  },
  {
    icon: CheckCircle,
    title: 'Reduced Admin Overhead',
    description: 'Eliminate manual paperwork and double data entry. Everything captured digitally from the field.',
  },
];

const industries = [
  {
    title: 'Mobile Crane Hire',
    description: 'Purpose-built for mobile crane hire operations. From quoting and allocations to job execution, compliance, and invoicing.',
    link: '/mobile-crane-hire-rigging',
    image: 'https://images.pexels.com/photos/28321426/pexels-photo-28321426.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    title: 'Operated Plant Hire',
    description: 'Complete visibility over assets, crews, and job schedules for earthmoving and plant hire operations.',
    link: '/plant-hire-software',
    image: 'https://images.unsplash.com/photo-1759579479871-19dfbc9c39ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx5ZWxsb3clMjBleGNhdmF0b3IlMjBkaWdnZXIlMjBjb25zdHJ1Y3Rpb24lMjBlcXVpcG1lbnR8ZW58MHx8fHwxNzczMjAwMTMyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    title: 'Civil Construction',
    description: 'Manage hundreds or thousands of assets from internal or external sources with complete audit trail.',
    link: '/civil-construction-operations-software',
    image: 'https://images.unsplash.com/photo-1517089152318-42ec560349c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYWVyaWFsJTIwdmlldyUyMGV4Y2F2YXRvcnMlMjBidWxsZG96ZXJzfGVufDB8fHx8MTc3MzIwMTAyNHww&ixlib=rb-4.1.0&q=85',
  },
  {
    title: 'Truck & Cartage',
    description: 'Streamline your transport operations with efficient dispatch, real-time tracking, and digital proof of delivery.',
    link: '/truck-cartage-software',
    image: 'https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    title: 'Concrete Pumping',
    description: 'Manage your concrete pumping fleet with volume tracking, pour scheduling, and integrated invoicing.',
    link: '/concrete-pumping-software',
    image: 'https://images.pexels.com/photos/585419/pexels-photo-585419.jpeg?auto=compress&cs=tinysrgb&w=800',
  },
  {
    title: 'Road Sweeping',
    description: 'Efficient route planning, GPS tracking, and recurring job management for sweeping contractors.',
    link: '/road-sweeping-software',
    image: 'https://images.unsplash.com/photo-1764981966665-1abf644d10a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzdHJlZXQlMjBzd2VlcGVyJTIwbXVuaWNpcGFsJTIwY2xlYW5pbmclMjB2ZWhpY2xlfGVufDB8fHx8MTc3MzIwMDEyN3ww&ixlib=rb-4.1.0&q=85',
  },
];

const Home = () => {
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      schemas.organization,
      schemas.website,
      schemas.softwareApplication(
        "EQUIPR Software",
        "Manage plant and equipment hire, operators, safety, and maintenance in one place. Say goodbye to multiple systems and manual processes.",
        "BusinessApplication"
      )
    ]
  };

  return (
    <div data-testid="home-page">
      <SEO 
        title="#1 Operated Plant & Equipment Hire Software Australia"
        description="Australia's leading operations management software for plant hire, mobile cranes, civil construction, and heavy equipment. Streamline scheduling, dispatch, compliance, and invoicing in one platform."
        keywords="plant hire software, equipment hire software, crane hire software, construction software Australia, operated plant hire, dispatch software, equipment management, fleet management software, heavy equipment software, operations management"
        schema={homeSchema}
        pageRoute="/"
      />

      {/* Hero Section - Dark with 2-column layout */}
      <section className="relative min-h-[90vh] flex items-center bg-equipr-dark overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-equipr-dark via-equipr-dark to-equipr-dark-secondary">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-equipr-yellow/5 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-equipr-yellow/10 rounded-full mb-6">
                <Sparkles className="h-5 w-5 text-equipr-yellow" />
                <span className="text-equipr-yellow font-medium text-sm uppercase tracking-wider">EQUIPR Software</span>
              </div>
              
              <p className="text-equipr-yellow uppercase tracking-widest text-sm font-semibold mb-4">
                Operations Software if you Lift, Shift, Dig, Cart, Sweep, Pump, Fix
              </p>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-equipr-yellow uppercase leading-tight mb-2">
                Plan Better.
              </h1>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase leading-tight mb-6">
                Work Better.
              </h1>
              
              <p className="text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
                EQUIPR is the leading software platform purpose-built for operated plant hire operations. 
                From quoting and allocations to job execution, compliance, and invoicing - bring your entire operation into one streamlined system.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-a-demo">
                  <Button 
                    className="bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover font-bold uppercase tracking-wide px-8 py-6 text-base btn-primary-glow"
                    data-testid="hero-demo-btn"
                  >
                    Request a Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact-us">
                  <Button 
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/5 font-bold uppercase tracking-wide px-8 py-6 text-base"
                    data-testid="hero-contact-btn"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right: Visual Element - Stats/Capability Grid */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: Calendar, label: 'Scheduling', value: 'Drag & Drop' },
                      { icon: FileText, label: 'Mobile Dockets', value: 'Paperless' },
                      { icon: Shield, label: 'Compliance', value: 'Automated' },
                      { icon: Receipt, label: 'Invoicing', value: 'Integrated' },
                    ].map((stat, index) => (
                      <motion.div 
                        key={index} 
                        className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-equipr-yellow/30 transition-colors"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                      >
                        <stat.icon className="h-8 w-8 text-equipr-yellow mb-2" />
                        <div className="text-white/60 text-xs uppercase tracking-wider">{stat.label}</div>
                        <div className="text-white font-bold text-lg">{stat.value}</div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-6 pt-6 border-t border-white/10 text-center">
                    <p className="text-white/50 text-sm">Trusted by 100+ companies across AU, NZ, UK & EU</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-equipr-yellow rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Key Capabilities Strip - Yellow Bar */}
      <section className="bg-equipr-yellow py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {platformHighlights.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <item.icon className="h-5 w-5 text-equipr-dark" />
                <span className="font-bold text-equipr-dark uppercase text-sm tracking-wider">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos Section - Light Gray */}
      <section className="py-16 bg-equipr-surface border-b border-equipr-light-gray overflow-hidden" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-8">
          <p className="text-center text-equipr-dark/50 uppercase tracking-widest text-sm font-medium">
            Trusted by Industry Leaders
          </p>
        </div>
        
        <div className="relative">
          <div className="flex animate-scroll-logos">
            {[...clientLogos, ...clientLogos].map((logo, index) => (
              <div key={index} className="flex-shrink-0 mx-8 md:mx-12">
                <img 
                  src={logo.url} 
                  alt={logo.name}
                  loading="lazy"
                  className="h-12 md:h-16 w-auto opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Capabilities Section - White */}
      <section className="py-24 md:py-32 bg-white" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-black text-equipr-dark uppercase mb-4">
              Key Capabilities
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Eliminate paperwork, improve team communication, and gain complete visibility over assets, crews, and job schedules.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`capability-card-${index}`}
              >
                <Link to={capability.link}>
                  <div className="bg-equipr-dark border border-gray-800 hover:border-equipr-yellow/50 p-8 rounded-xl transition-all duration-300 h-full group hover:shadow-lg">
                    <div className="w-14 h-14 bg-equipr-yellow/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-equipr-yellow/20 transition-colors">
                      <capability.icon className="h-7 w-7 text-equipr-yellow" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white uppercase mb-3 group-hover:text-equipr-yellow transition-colors">
                      {capability.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed text-sm">
                      {capability.description}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features & Benefits Section - Dark */}
      <section className="py-24 md:py-32 bg-equipr-dark" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-black text-white uppercase mb-4">
              Benefits
            </h2>
            <p className="text-white/60 text-lg max-w-3xl mx-auto">
              The result? Smarter resource utilisation, faster decision-making, and less admin for your office and field teams.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuresAndBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 border border-white/20 hover:border-equipr-yellow/50 transition-colors"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-12 h-12 bg-equipr-yellow/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-equipr-dark" />
                </div>
                <h3 className="font-heading text-lg font-bold text-equipr-dark uppercase mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section - White */}
      <section className="py-24 md:py-32 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-black text-equipr-dark uppercase mb-4">
              Industries We Serve
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Purpose-built solutions for equipment hire and operations businesses across Australia,{' '}
              <span className="whitespace-nowrap">New Zealand</span>, UK, and Europe.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group"
                data-testid={`industry-card-${index}`}
              >
                <Link to={industry.link}>
                  <div className="bg-gray-50 border border-gray-100 hover:border-equipr-yellow/50 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg">
                    <div className="aspect-video overflow-hidden relative">
                      <img 
                        src={industry.image} 
                        alt={industry.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-equipr-dark/60 via-transparent to-transparent" />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-equipr-dark uppercase mb-3 group-hover:text-equipr-yellow transition-colors">
                        {industry.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {industry.description}
                      </p>
                      <span className="inline-flex items-center gap-2 text-equipr-yellow text-sm font-semibold uppercase tracking-wide group-hover:gap-3 transition-all">
                        Learn More <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why EQUIPR Section - Light Gray */}
      <section className="py-24 md:py-32 bg-gray-50" data-testid="why-equipr-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-black text-equipr-dark uppercase mb-8">
                Why Choose EQUIPR?
              </h2>
              <div className="space-y-6">
                {[
                  { title: 'User-Friendly Interface', desc: 'Intuitive design that your team can learn quickly without extensive training.' },
                  { title: 'Designed for Your Industry', desc: 'Built specifically for mobile crane hire, plant hire, and civil construction - not generic software adapted for your needs.' },
                  { title: 'Australian-Based Support', desc: 'Local support team with deep industry knowledge. We understand your business.' },
                  { title: 'Accounting Integration', desc: 'Seamless integration with most common accounting software via API including Xero, MYOB, and more.' },
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-8 h-8 bg-equipr-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-5 w-5 text-equipr-yellow" />
                    </div>
                    <div>
                      <h3 className="text-equipr-dark font-bold mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="EQUIPR Software Dashboard"
                loading="lazy"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-equipr-yellow p-6 rounded-xl shadow-lg">
                <p className="font-heading text-equipr-dark text-3xl font-bold">15+</p>
                <p className="text-equipr-dark/80 text-sm">Years in Industry</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Us Section - Dark */}
      <section className="py-24 md:py-32 bg-equipr-dark" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-black text-white uppercase mb-4">
              Contact Us
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Get in touch with our team for a free consultation and to find out how EQUIPR can help your business.
            </p>
          </motion.div>

          <div className="max-w-lg mx-auto">
            {/* Aus/NZ Office */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8"
            >
              <h3 className="font-heading text-xl font-bold text-equipr-dark uppercase mb-6">Aus / NZ Head Office</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-equipr-yellow/10 rounded-lg flex items-center justify-center">
                    <Phone className="h-5 w-5 text-equipr-yellow" />
                  </div>
                  <span className="text-gray-700">1300 100 365</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-equipr-yellow/10 rounded-lg flex items-center justify-center">
                    <Mail className="h-5 w-5 text-equipr-yellow" />
                  </div>
                  <span className="text-gray-700">info@equipr.software</span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-equipr-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-equipr-yellow" />
                  </div>
                  <div className="text-gray-600 text-sm">
                    <p>EQUIPR Software Pty Ltd</p>
                    <p>UniSC – Innovation Centre</p>
                    <p>90 Sippy Downs Drive</p>
                    <p>SIPPY DOWNS, QLD 4556</p>
                    <p>AUSTRALIA</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm">Office hours: 9:00 – 17:00 Mon to Fri</p>
              </div>

              <div className="border-t border-gray-200 my-6"></div>

              <h3 className="font-heading text-xl font-bold text-equipr-dark uppercase mb-6">UK / EU Office</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-equipr-yellow/10 rounded-lg flex items-center justify-center">
                    <Mail className="h-5 w-5 text-equipr-yellow" />
                  </div>
                  <span className="text-gray-700">info.eu@equipr.software</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - White */}
      <section className="py-24 md:py-32 bg-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-black text-equipr-dark uppercase mb-6">
              Ready to Streamline Your Operations?
            </h2>
            <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
              Learn more about how industry leaders are using EQUIPR to improve efficiency, reduce admin, and boost profitability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-a-demo">
                <Button 
                  className="bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover font-bold uppercase tracking-wide px-10 py-6 text-base btn-primary-glow"
                  data-testid="cta-demo-btn"
                >
                  Request a Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact-us">
                <Button 
                  variant="outline"
                  className="border-equipr-dark/20 text-equipr-dark hover:bg-equipr-dark/5 font-bold uppercase tracking-wide px-10 py-6 text-base"
                  data-testid="cta-contact-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
